import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useStore } from 'effector-react';
import { Button, Modal, Table, Spin, Empty } from 'antd';
import moment from 'moment';

import IUser from '@interfaces/IUser';
import useTranslate from '@hooks/useTranslate';
import componentContent from '@components/SpamUserCard/SpamUserCard.content.json';
import userMessages, {
  getList as getUserMessages,
  reset as resetUserMessages,
} from '@stores/user-messages';
import css from './UserMessagesTrigger.module.less';

interface UserMessagesTriggerProps {
  userId?: IUser['id'];
}
const UserMessagesTrigger: FunctionComponent<UserMessagesTriggerProps> = ({ userId }) => {
  const content = useTranslate(componentContent);
  const { list } = useStore(userMessages);

  const [isOpened, setIsOpened] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const handleOpen = useCallback(async () => {
    if (!userId) return;

    resetUserMessages();
    setIsOpened(true);
    setIsModalLoading(true);

    try {
      await getUserMessages(userId);
    } finally {
      setIsModalLoading(false);
    }
  }, [userId]);

  const handleClose = useCallback(() => {
    setIsOpened(false);
    setIsModalLoading(false);
  }, []);

  const messageColumns = useMemo(
    () => [
      {
        key: 'messageCreatedAt',
        dataIndex: 'messageCreatedAt',
        title: content.created,
        render: (value: string) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : '-'),
      },
      {
        key: 'messageText',
        dataIndex: 'messageText',
        title: content.text,
        render: (value: string) => <div className={css.messagesTableText}>{value || '-'}</div>,
      },
      {
        key: 'recipient',
        dataIndex: 'recipient',
        title: content.recipient,
        render: (value: string) => value || '-',
      },
      {
        key: 'claim',
        dataIndex: 'claim',
        title: content.suspiciousFlag,
        render: (value: any) => (value ? content.yes : content.no),
      },
    ],
    [content],
  );

  return (
    <>
      <Button
        size='small'
        onClick={handleOpen}
      >
        {content.viewMessages}
      </Button>

      <Modal
        visible={isOpened}
        title={content.viewMessages}
        footer={null}
        onCancel={handleClose}
        width={1100}
        destroyOnClose
        keyboard
      >
        {isModalLoading ? (
          <div
            style={{
              minHeight: 240,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin size='large' />
          </div>
        ) : list.length > 0 ? (
          <Table
            rowKey='messageId'
            dataSource={list}
            columns={messageColumns}
            pagination={{
              pageSizeOptions: ['10', '20', '30'],
              showSizeChanger: true,
              position: ['topRight'],
              defaultPageSize: 10,
            }}
          />
        ) : (
          <Empty imageStyle={{ height: 50 }} />
        )}
      </Modal>
    </>
  );
};

export default UserMessagesTrigger;
